import { Button, Empty, Form, message, Select, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { formHasErrors } from '../../../../shared/utils/functions/handleFormChanges';
import { IJobListFilter } from '../../../../shared/utils/interfaces/jobListFaces';
import { useDispatch, useSelector } from 'react-redux';
import { selectFilters } from '../../../viewJobs/slice/jobListSlice';
import ChevronDownGray from '../../../../shared/assets/images/ChevronDownGray.svg';
import { useGetJobsListQuery } from '../../../viewJobs/slice/jobListSliceApi';
import { IJob } from '../../../../shared/utils/interfaces/settingsInterface';
import { errorMessage } from '../../../../shared/utils/constantData';
import { useGetStagesByJobIdQuery } from '../../slice/createJobApiSlice';
import { selectCandidateProfile, selectCandidatesFilters, setCandidatesFilter, setCandidatesModalOpen, setStageChange } from '../../slice/candidateSlice';
import { useChangeCandidateJobMutation } from '../../slice/candiddateApiSlice';
import { LoadingOutlined } from '@ant-design/icons';
import { IGlobalStageType } from '../../../../shared/utils/interfaces/pipelineInterface';
import { candidatesSorting, GlobalStageTypesId } from '../../../../shared/utils/interfaces/candidatesListFace';
import { useCandidateNavigation } from '../../../../shared/utils/customHooks/useCandidateNavigation';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { relocateNotification } from '../../../../shared/utils/functions/relocateNotification';
import { setMobileTabsMenuExpanded } from '../../../../shared/components/Menu/slice/tabsMenuSlice';

export default function MoveCandidateForm({ isProfilePage }: { isProfilePage: boolean }) {
    const { Option } = Select;
    const location = useLocation()
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isDisabled, setIsDisabled] = useState(true);
    const [selectedJobId, setSelectedJobId] = useState<number>();
    const [selectedJobTitle, setSelectedJobTitle] = useState<string>();
    const [selectedStage, setSelectedStage] = useState<IGlobalStageType>();
    const { shortVersion, page, jobsPerPage }: IJobListFilter = useSelector(selectFilters);
    const candidate = useSelector(selectCandidateProfile);
    const { isLoading: isLoadingJobs, isError: isErrorJobs, data: jobsData } = useGetJobsListQuery({ shortVersion, page, jobsPerPage, hideDrafts: true });
    const { isLoading: isLoadingStages, isError: isErrorStages, data: stagesData } = useGetStagesByJobIdQuery({ jobId: selectedJobId }, { skip: !selectedJobId });
    const [changeCandidateJob, { isLoading: isLoadingMovingCandidate, isError: isErrorMovingCandidate, isSuccess: isSuccessMovingCandidate }] = useChangeCandidateJobMutation();
    const [isMessageShown, setIsMessageShown] = useState(false);
    const { handleCandidateNavigate } = useCandidateNavigation()
    const { jobId } = useParams();
    const candidatesFilter = useSelector(selectCandidatesFilters);

    useEffect(() => {
        if (isErrorJobs) message.error(errorMessage)
    }, [isErrorJobs])

    useEffect(() => {
        if (isErrorStages) message.error(errorMessage)
    }, [isErrorStages])

    const relocateCandidate = () => {
        dispatch(setCandidatesFilter({ ...candidatesFilter, hiringStageId: selectedStage?.id, sortBy: candidatesSorting[0].value }));
        if (!isProfilePage) dispatch(setMobileTabsMenuExpanded(null));
        navigate(`/jobs/${selectedJobId}/candidates?hiringStageId=${selectedStage?.id}`);
    }

    useEffect(() => {
        if (isSuccessMovingCandidate && !isMessageShown) {
            dispatch(setCandidatesModalOpen(false))
            relocateNotification({ candidate, destination: `${selectedJobTitle} / ${selectedStage?.name}`, isStage: true, onClick: relocateCandidate });
            const selectedStageGlobalTypeId = stagesData?.data?.find(({ id }: IGlobalStageType) => id === selectedStage?.id)?.global_stage_type_id;
            
            const isSameJob = Number(jobId) === selectedJobId;
            const isRejectedStage = selectedStageGlobalTypeId === GlobalStageTypesId.rejected;
            const fromSearch = location?.state?.fromSearch;
            const shouldNavigate = !fromSearch && isProfilePage;

            if (isSameJob) {
                if (isRejectedStage && shouldNavigate) {
                    localStorage.removeItem("currentTab");
                    handleCandidateNavigate();
                } else {
                    dispatch(setStageChange(selectedStageGlobalTypeId));
                }
            } else if (shouldNavigate && !isSameJob) {
                handleCandidateNavigate();
            }
            form.resetFields();
            setIsMessageShown(true);
        } else if (isErrorMovingCandidate) message.error(errorMessage)

        // eslint-disable-next-line
    }, [isErrorMovingCandidate, isSuccessMovingCandidate, stagesData, dispatch, form, selectedJobTitle, selectedStage, isProfilePage, isMessageShown])

    const handleFormChange = () => {
        setIsDisabled(formHasErrors(form));
    }

    const handleSelectJob = (id: number, option: { title: string }) => {
        setSelectedJobId(id)
        setSelectedJobTitle(option?.title)
    }
    const handleSelectStage = (id: number, option: { name: string, global_stage_type_id: number }) => {
        setSelectedStage({ id, name: option?.name, global_stage_type_id: option?.global_stage_type_id })
    }

    async function onFinish(values: any) {
        const { id, jobId } = candidate
        await changeCandidateJob({ currentJobId: jobId, applicationId: id, ...values })
    }
    useEffect(() => {
        if (!isSuccessMovingCandidate) {
            setIsMessageShown(false);
        }
    }, [isSuccessMovingCandidate, isMessageShown]);

    return (
        <Form
            form={form}
            className='text-start'
            size='large'
            onFinish={onFinish}
            onFieldsChange={handleFormChange}
        >
            <Form.Item
                name="newJobId"
                rules={[{ required: true, message: 'Job is missing' }]}
            >
                <Select
                    notFoundContent={isLoadingJobs ? <Spin indicator={<LoadingOutlined spin />} size="large" className='flex items-center justify-center m-10 text-gray-600' /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                    className='w-full text-sm font-normal text-gray-600 placeholder-gray-400 capitalize'
                    placeholder="Select a job"
                    suffixIcon={<img src={ChevronDownGray} alt='Arrow Down' />}
                    onSelect={handleSelectJob}
                    disabled={isErrorJobs}
                >
                    {jobsData?.jobs?.map(({ id, title }: IJob) => {
                        return (<Option key={id} value={id} title={title}>{title}</Option>)
                    })}
                </Select>
            </Form.Item>
            <Form.Item
                name="pipelineStageId"
                rules={[{ required: true, message: 'Stage is missing' }]}
            >
                <Select
                    notFoundContent={isLoadingStages ? <Spin indicator={<LoadingOutlined spin />} size="large" className='flex items-center justify-center m-10 text-gray-600' /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                    className='w-full text-sm font-normal text-gray-600 placeholder-gray-400 capitalize'
                    placeholder="Select a stage"
                    suffixIcon={<img src={ChevronDownGray} alt='Arrow Down' />}
                    disabled={!selectedJobId || isErrorStages}
                    onSelect={handleSelectStage}
                >
                    {stagesData?.data?.map(({ id, name }: { id: number, name: string }) => {
                        return (<Option key={id} value={id} name={name}>{name}</Option>)
                    })}
                </Select>
            </Form.Item>
            <Form.Item
                className='mb-0'
            >
                <Button
                    loading={isLoadingMovingCandidate}
                    disabled={isDisabled || isLoadingMovingCandidate}
                    className="w-full btn-primary btn-lg"
                    htmlType='submit'
                >
                    Move
                </Button>
            </Form.Item>
        </Form>
    )
}